import * as React from 'react';
import { responsiveFontSize, responsiveIconSize } from '../../util/responsiveUtils';
import {
  Box,
  InputBase,
  IconButton,
  Popover,
  List,
  ListItem,
  ListItemText,
  Typography,
  Divider,
  Collapse,
  useMediaQuery,
  useTheme,
  alpha,
} from '@mui/material';
import { ContentCard } from '../common/ContentCard';
import SearchIcon from '@mui/icons-material/Search';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import CloseIcon from '@mui/icons-material/Close';
import ExpandLess from '@mui/icons-material/ExpandLess';
import ExpandMore from '@mui/icons-material/ExpandMore';
import { getButtonColor, getIconLightColor } from '../../theme/themeUtils';
import { Z_INDEX } from '../../constants/zIndex';
import { CANADA_DATA } from '../../data/CanadianLocations';
import { useLocation } from '../../context/LocationContext';
import { useNavigate } from 'react-router';

interface SearchBarProps {
    onCitySelect?: (city: string) => void;
    /** When provided, called with optional pending search when user should open the city modal (e.g. search without location). */
    onOpenCityModal?: (pendingSearch?: string) => void;
}

export function SearchBar({ onCitySelect, onOpenCityModal }: SearchBarProps) {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const [locationAnchorEl, setLocationAnchorEl] = React.useState<HTMLElement | null>(null);
    const [searchText, setSearchText] = React.useState('');
    const [cityFilterText, setCityFilterText] = React.useState('');
    const [expandedProvinces, setExpandedProvinces] = React.useState<Record<string, boolean>>({});
    const { city, setLocation } = useLocation();
    const selectedCity = city;
    const navigate = useNavigate();

    // Popular cities list
    const popularCities = [
        "Toronto",
        "Vancouver",
        "Calgary",
        "Edmonton",
        "Montreal",
        "Ottawa",
        "Mississauga",
        "Brampton",
        "Hamilton",
        "Winnipeg",
    ];

    const handleLocationClick = (event: React.MouseEvent<HTMLElement>) => {
        setLocationAnchorEl(event.currentTarget);
    };

    const handleLocationClose = () => {
        setLocationAnchorEl(null);
        setCityFilterText('');
    };

    const handleCitySelect = (city: string) => {
        // Find the province for the selected city
        const selectedProvince = CANADA_DATA.provinces.find(p =>
            p.cities.includes(city)
        );

        if (selectedProvince) {
            setLocation(city, selectedProvince.name);
            if (onCitySelect) {
                onCitySelect(city);
            }
            handleLocationClose();
        }
    };

    const toggleProvince = (provinceName: string) => {
        setExpandedProvinces(prev => ({
            ...prev,
            [provinceName]: !prev[provinceName]
        }));
    };

    const handleSearch = () => {
        const query = searchText.trim();
        if (!query) return;
        if (!selectedCity) {
            onOpenCityModal?.(query);
            return;
        }
        navigate(`/find-experts-in-city?search=${encodeURIComponent(query)}`);
        setSearchText('');
    };

    // Filter provinces and cities in the location popover (separate from main search)
    const filteredProvinces = React.useMemo(() => {
        if (!cityFilterText) return CANADA_DATA.provinces;

        const searchLower = cityFilterText.toLowerCase();
        return CANADA_DATA.provinces
            .map(province => ({
                ...province,
                cities: province.cities.filter(city =>
                    city.toLowerCase().includes(searchLower)
                )
            }))
            .filter(province => province.cities.length > 0);
    }, [cityFilterText]);

    return (
        <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
            <ContentCard
                sx={{ 
                    p: { xs: '2px 2px', sm: '2px 4px' }, 
                    display: 'flex', 
                    alignItems: 'center', 
                    width: '100%',
                    minWidth: { xs: 180, sm: 220, md: 260, lg: 300 },
                    overflow: 'hidden'
                }}
            >
                <Box
                    component="form"
                    onSubmit={(e) => {
                        e.preventDefault();
                        handleSearch();
                    }}
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        width: '100%',
                        gap: { xs: 0.25, sm: 0.5, md: 0.75 },
                    }}
                >
                <IconButton 
                    type="submit" 
                    sx={{ 
                        p: { xs: '6px', sm: '8px', md: '10px' },
                        minWidth: { xs: 32, sm: 40 },
                        flexShrink: 0,
                        '@media (hover: hover)': {
                            '&:hover': {
                                backgroundColor: 'transparent'
                            }
                        }
                    }} 
                    aria-label="search"
                >
                    <SearchIcon sx={{ fontSize: responsiveIconSize.icon16_18_20 }} />
                </IconButton>
                <InputBase
                    sx={{ 
                        flex: 1,
                        fontSize: responsiveFontSize.bodySmallToBase,
                        minWidth: 0,
                        '& input': {
                            fontSize: responsiveFontSize.bodySmallToBase,
                            wordBreak: 'break-word',
                            overflowWrap: 'break-word'
                        }
                    }}
                    placeholder={isMobile ? "Search..." : "Search anything..."}
                    inputProps={{ 'aria-label': 'search' }}
                    value={searchText}
                    onChange={(e) => setSearchText(e.target.value)}
                />
                {!selectedCity && (
                    <IconButton
                        sx={{
                            p: { xs: '6px', sm: '8px', md: '10px' },
                            minWidth: { xs: 32, sm: 40 },
                            flexShrink: 0,
                            '@media (hover: hover)': {
                                '&:hover': {
                                    backgroundColor: 'transparent',
                                },
                            },
                        }}
                        aria-label="Choose your city"
                        onClick={handleLocationClick}
                    >
                        <LocationOnIcon sx={{ fontSize: responsiveIconSize.icon16_18_20 }} />
                    </IconButton>
                )}
                {selectedCity && (
                    <Box
                        component="button"
                        type="button"
                        onClick={handleLocationClick}
                        aria-label={`Change city. Current city: ${selectedCity}`}
                        sx={{
                            minWidth: 0,
                            maxWidth: { xs: 220, sm: 260, md: 300 },
                            flexShrink: 1,
                            pl: { xs: 0.75, sm: 1 },
                            pr: { xs: 1, sm: 1.25 },
                            py: { xs: 0.35, sm: 0.45 },
                            gap: { xs: 0.5, sm: 0.625 },
                            borderRadius: 999,
                            backgroundColor: alpha(getButtonColor(theme), 0.14),
                            border: `1px solid ${alpha(getButtonColor(theme), 0.22)}`,
                            display: 'inline-flex',
                            alignItems: 'center',
                            alignSelf: 'center',
                            cursor: 'pointer',
                            font: 'inherit',
                            color: 'inherit',
                            m: 0,
                            textAlign: 'left',
                            '@media (hover: hover)': {
                                '&:hover': {
                                    backgroundColor: alpha(getButtonColor(theme), 0.22),
                                },
                            },
                            '&:focus-visible': {
                                outline: `2px solid ${getButtonColor(theme)}`,
                                outlineOffset: 2,
                            },
                        }}
                    >
                        <LocationOnIcon
                            sx={{
                                fontSize: responsiveIconSize.icon16_18_20,
                                flexShrink: 0,
                                color: getButtonColor(theme),
                                display: 'block',
                            }}
                        />
                        <Typography
                            variant="body2"
                            component="span"
                            sx={{
                                minWidth: 0,
                                fontSize: {
                                    xs: responsiveFontSize.bodyMenu.xs,
                                    sm: responsiveFontSize.bodyMenu.sm,
                                    md: responsiveFontSize.overline3Breakpoints.md,
                                },
                                fontWeight: 600,
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                                color: getButtonColor(theme),
                                lineHeight: 1.25,
                            }}
                        >
                            {selectedCity}
                        </Typography>
                    </Box>
                )}
                </Box>
            </ContentCard>

            <Popover
                open={Boolean(locationAnchorEl)}
                anchorEl={locationAnchorEl}
                onClose={handleLocationClose}
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: isMobile ? 'center' : 'left',
                }}
                transformOrigin={{
                    vertical: 'top',
                    horizontal: isMobile ? 'center' : 'left',
                }}
                sx={{ zIndex: Z_INDEX.DIALOG }}
                slotProps={{
                    backdrop: {
                        sx: {
                            backgroundColor: theme.palette.mode === 'dark'
                                ? 'rgba(0, 0, 0, 0.7)'
                                : 'rgba(0, 0, 0, 0.5)',
                            backdropFilter: 'blur(4px)',
                        },
                    },
                    paper: {
                        sx: {
                            borderRadius: { xs: 2, sm: 3 },
                            backgroundColor: theme.palette.background.paper,
                            backgroundImage: 'none',
                            width: { xs: 'calc(100vw - 32px)', sm: 400 },
                            maxWidth: { xs: 320, sm: 400 },
                            maxHeight: '45vh',
                            boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
                            display: 'flex',
                            flexDirection: 'column',
                            overflow: 'hidden',
                            mt: 1,
                        },
                    },
                }}
            >
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        py: { xs: 1.5, sm: 2 },
                        px: { xs: 2, sm: 3 },
                        color: theme.palette.text.primary,
                        flexShrink: 0,
                        borderBottom: `1px solid ${theme.palette.divider}`,
                        background: `linear-gradient(160deg, ${alpha(getButtonColor(theme), 0.08)} 0%, ${alpha(theme.palette.secondary.main, 0.04)} 50%, transparent 100%)`,
                    }}
                >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 1, sm: 1.5 } }}>
                        <Box
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                width: { xs: 36, sm: 40 },
                                height: { xs: 36, sm: 40 },
                                borderRadius: 2,
                                bgcolor: alpha(getButtonColor(theme), 0.12),
                                color: getButtonColor(theme),
                            }}
                        >
                            <LocationOnIcon sx={{ fontSize: responsiveIconSize.md }} />
                        </Box>
                        <Typography
                            variant={isMobile ? 'subtitle1' : 'h6'}
                            component="div"
                            sx={{
                                fontWeight: 600,
                                color: theme.palette.text.primary,
                                wordBreak: 'break-word',
                                overflowWrap: 'break-word',
                            }}
                        >
                            Which city are you in?
                        </Typography>
                    </Box>
                    <IconButton
                        onClick={handleLocationClose}
                        size={isMobile ? 'medium' : 'small'}
                        sx={{
                            color: theme.palette.text.secondary,
                            '@media (hover: hover)': {
                                '&:hover': {
                                    backgroundColor: alpha(getIconLightColor(theme), 0.2),
                                },
                            },
                        }}
                    >
                        <CloseIcon />
                    </IconButton>
                </Box>

                <Box
                    sx={{
                        px: { xs: 2, sm: 3 },
                        pt: { xs: 2, sm: 2.5 },
                        pb: { xs: 2, sm: 3 },
                        overflowY: 'auto',
                        overflowX: 'hidden',
                        flex: 1,
                        minHeight: 0,
                    }}
                >
                    <ContentCard
                        sx={{
                            p: { xs: '2px 2px', sm: '2px 4px' },
                            display: 'flex',
                            alignItems: 'center',
                            mb: { xs: 1.5, sm: 2 },
                            overflow: 'hidden',
                        }}
                    >
                        <InputBase
                            sx={{
                                ml: { xs: 0.5, sm: 1 },
                                flex: 1,
                                fontSize: responsiveFontSize.body,
                                '& input': {
                                    fontSize: responsiveFontSize.body,
                                    wordBreak: 'break-word',
                                    overflowWrap: 'break-word',
                                },
                            }}
                            placeholder="Search city..."
                            value={cityFilterText}
                            onChange={(e) => setCityFilterText(e.target.value)}
                        />
                        <IconButton
                            type="button"
                            sx={{ p: { xs: '8px', sm: '10px' }, minWidth: { xs: 36, sm: 40 } }}
                            aria-label="search"
                        >
                            <SearchIcon sx={{ fontSize: responsiveIconSize.sm }} />
                        </IconButton>
                    </ContentCard>

                    {!cityFilterText.trim() && (
                        <>
                            <Typography
                                variant="subtitle1"
                                sx={{
                                    mb: { xs: 0.75, sm: 1 },
                                    fontSize: responsiveFontSize.bodyCompactToLarge,
                                    fontWeight: 600,
                                    color: theme.palette.text.primary,
                                    wordBreak: 'break-word',
                                    overflowWrap: 'break-word',
                                }}
                            >
                                Popular cities
                            </Typography>
                            <Box
                                sx={{
                                    display: 'flex',
                                    flexWrap: 'wrap',
                                    gap: { xs: 0.75, sm: 1 },
                                    mb: { xs: 1.5, sm: 2 },
                                }}
                            >
                                {popularCities.map((cityName) => (
                                    <ContentCard
                                        key={cityName}
                                        sx={{
                                            px: { xs: 1, sm: 1.5 },
                                            py: { xs: 0.5, sm: 0.5 },
                                            cursor: 'pointer',
                                            transition: 'all 0.2s ease',
                                            '@media (hover: hover)': {
                                                '&:hover': {
                                                    backgroundColor: alpha(getButtonColor(theme), 0.08),
                                                    transform: { xs: 'none', sm: 'translateY(-1px)' },
                                                },
                                            },
                                        }}
                                        onClick={() => handleCitySelect(cityName)}
                                    >
                                        <Typography
                                            variant="body2"
                                            sx={{
                                                fontSize: responsiveFontSize.bodySmall,
                                                wordBreak: 'break-word',
                                                overflowWrap: 'break-word',
                                                color: theme.palette.text.primary,
                                            }}
                                        >
                                            {cityName}
                                        </Typography>
                                    </ContentCard>
                                ))}
                            </Box>

                            <Typography
                                variant="subtitle1"
                                sx={{
                                    mb: { xs: 0.75, sm: 1 },
                                    fontSize: responsiveFontSize.bodyCompactToLarge,
                                    fontWeight: 600,
                                    color: theme.palette.text.primary,
                                    wordBreak: 'break-word',
                                    overflowWrap: 'break-word',
                                }}
                            >
                                Provinces
                            </Typography>
                        </>
                    )}

                    {cityFilterText.trim() && (
                        <Typography
                            variant="subtitle1"
                            sx={{
                                mb: { xs: 0.75, sm: 1 },
                                fontSize: responsiveFontSize.bodyCompactToLarge,
                                fontWeight: 600,
                                color: theme.palette.text.primary,
                                wordBreak: 'break-word',
                                overflowWrap: 'break-word',
                            }}
                        >
                            Results
                        </Typography>
                    )}
                    <List dense sx={{ p: 0 }}>
                        {filteredProvinces.map((province) => (
                            <React.Fragment key={province.name}>
                                <ListItem
                                    onClick={() => toggleProvince(province.name)}
                                    sx={{
                                        p: { xs: '6px 0', sm: '4px 0' },
                                        cursor: 'pointer',
                                        borderRadius: 1,
                                        '@media (hover: hover)': {
                                            '&:hover': {
                                                backgroundColor: alpha(getButtonColor(theme), 0.08),
                                            },
                                        },
                                    }}
                                >
                                    <ListItemText
                                        primary={province.name}
                                        primaryTypographyProps={{
                                            sx: {
                                                fontSize: responsiveFontSize.body,
                                                wordBreak: 'break-word',
                                                overflowWrap: 'break-word',
                                                color: theme.palette.text.primary,
                                            },
                                        }}
                                    />
                                    {expandedProvinces[province.name] ? (
                                        <ExpandLess sx={{ fontSize: responsiveIconSize.sm, color: theme.palette.text.secondary }} />
                                    ) : (
                                        <ExpandMore sx={{ fontSize: responsiveIconSize.sm, color: theme.palette.text.secondary }} />
                                    )}
                                </ListItem>
                                <Collapse in={expandedProvinces[province.name] || cityFilterText.length > 0} timeout="auto" unmountOnExit>
                                    <List component="div" disablePadding dense>
                                        {province.cities.map((cityName) => (
                                            <ListItem
                                                key={cityName}
                                                onClick={() => handleCitySelect(cityName)}
                                                sx={{
                                                    pl: { xs: 1.5, sm: 2 },
                                                    cursor: 'pointer',
                                                    borderRadius: 1,
                                                    '@media (hover: hover)': {
                                                        '&:hover': {
                                                            backgroundColor: alpha(getButtonColor(theme), 0.08),
                                                        },
                                                    },
                                                }}
                                            >
                                                <ListItemText
                                                    primary={cityName}
                                                    primaryTypographyProps={{
                                                        sx: {
                                                            fontSize: responsiveFontSize.bodyFixed,
                                                            wordBreak: 'break-word',
                                                            overflowWrap: 'break-word',
                                                            color: theme.palette.text.primary,
                                                        },
                                                    }}
                                                />
                                            </ListItem>
                                        ))}
                                    </List>
                                </Collapse>
                                <Divider sx={{ my: { xs: 0.75, sm: 1 } }} />
                            </React.Fragment>
                        ))}
                    </List>
                </Box>
            </Popover>
        </Box>
    );
}